export type SyncState = "synced" | "syncing" | "conflict" | "offline" | "local";

/** Map real auth + sync-queue state to a badge state. Passcode/anon users are
 *  device-local by design (they never authenticate, so cloud writes are blocked);
 *  authenticated users show "syncing" while the queue drains, else "synced". */
export function deriveSyncState(input: {
  passcodeMode: boolean;
  pendingCount: number;
}): SyncState {
  if (input.passcodeMode) return "local";
  if (input.pendingCount > 0) return "syncing";
  return "synced";
}

export function SyncStatus({
  state,
  lastSync,
}: {
  state: SyncState;
  lastSync?: Date;
}) {
  const labels: Record<SyncState, { text: string; bg: string; fg: string }> = {
    synced: {
      text: lastSync ? `Synced · ${formatAge(lastSync)}` : "Synced",
      bg: "hsl(var(--c-success))",
      fg: "hsl(var(--c-success-fg))",
    },
    syncing: {
      text: "Syncing…",
      bg: "hsl(var(--c-info))",
      fg: "hsl(var(--c-info-fg))",
    },
    conflict: {
      text: "Conflict detected",
      bg: "hsl(var(--c-destructive))",
      fg: "hsl(var(--c-destructive-fg))",
    },
    offline: {
      text: "Offline",
      bg: "hsl(var(--c-warning))",
      fg: "hsl(var(--c-warning-fg))",
    },
    local: {
      text: "Local only",
      bg: "hsl(var(--c-foreground-muted))",
      fg: "hsl(var(--c-background))",
    },
  };

  const { text, bg, fg } = labels[state];
  return (
    <span
      className="p-micro"
      aria-label={text}
      title={text}
      style={{
        color: fg,
        background: bg,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 999,
        fontWeight: 500,
        lineHeight: 1,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: fg,
          display: "inline-block",
        }}
      />
      {text}
    </span>
  );
}

function formatAge(d: Date): string {
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}
